import { pgTable, text, varchar, serial, integer, boolean, timestamp, doublePrecision, pgEnum } from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// === ENUMS ===
export const userRoleEnum = pgEnum("user_role", ["user", "business_owner", "admin", "drabpe_team", "drabpe_board"]);
export const businessStatusEnum = pgEnum("business_status", ["pending", "approved", "rejected"]);
export const notificationTypeEnum = pgEnum("notification_type", [
  "business_approved",
  "business_rejected",
  "new_review",
  "new_comment",
  "comment_reply",
  "new_submission",
]);
export const adPlacementEnum = pgEnum("ad_placement", ["banner_top", "banner_bottom", "inline", "sidebar"]);

// === TABLES ===

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  role: userRoleEnum("role").default("user").notNull(),
  avatarUrl: text("avatar_url"),
  forcePasswordReset: boolean("force_password_reset").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const categories = pgTable("categories", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
  slug: text("slug").notNull().unique(),
  icon: text("icon"),
  description: text("description"),
});

export const businesses = pgTable("businesses", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  description: text("description").notNull(),
  categoryId: integer("category_id").references(() => categories.id),
  address: text("address"),
  city: text("city"),
  state: text("state"),
  country: text("country"),
  postalCode: text("postal_code"),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  phone: text("phone"),
  email: text("email"),
  hideEmail: boolean("hide_email").default(false),
  website: text("website"),
  logoUrl: text("logo_url"),
  imageUrls: text("image_urls").array(),
  galleryUrls: text("gallery_urls").array(),
  galleryApproved: boolean("gallery_approved").default(false),
  tags: text("tags").array(),
  businessHours: text("business_hours"),
  linkedinUrl: text("linkedin_url"),
  facebookUrl: text("facebook_url"),
  instagramUrl: text("instagram_url"),
  isOnline: boolean("is_online").default(false),
  isInPerson: boolean("is_in_person").default(true),
  status: businessStatusEnum("status").default("pending").notNull(),
  isArchived: boolean("is_archived").default(false),
  isFeatured: boolean("is_featured").default(false),
  isSponsored: boolean("is_sponsored").default(false),
  ownerId: integer("owner_id").references(() => users.id),
  ownerPhotoUrl: text("owner_photo_url"),
  ownerDisplayName: text("owner_display_name"),
  avgRating: doublePrecision("avg_rating").default(0),
  reviewCount: integer("review_count").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const reviews = pgTable("reviews", {
  id: serial("id").primaryKey(),
  businessId: integer("business_id").notNull().references(() => businesses.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  rating: integer("rating").notNull(),
  comment: text("comment"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const comments = pgTable("comments", {
  id: serial("id").primaryKey(),
  businessId: integer("business_id").notNull().references(() => businesses.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  parentId: integer("parent_id"),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const ads = pgTable("ads", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  imageUrl: text("image_url"),
  linkUrl: text("link_url").notNull(),
  placement: adPlacementEnum("placement").default("banner_top").notNull(),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const notifications = pgTable("notifications", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: notificationTypeEnum("type").notNull(),
  message: text("message").notNull(),
  isRead: boolean("is_read").default(false),
  entityId: integer("entity_id"),
  entityType: text("entity_type"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const pushSubscriptions = pgTable("push_subscriptions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  endpoint: text("endpoint").notNull().unique(),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const loginHistory = pgTable("login_history", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  device: text("device"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const apiKeys = pgTable("api_keys", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  keyHash: text("key_hash").notNull(),
  last4: text("last4").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  lastUsedAt: timestamp("last_used_at"),
  revokedAt: timestamp("revoked_at"),
});

export const loginBlocks = pgTable("login_blocks", {
  id: serial("id").primaryKey(),
  ipAddress: text("ip_address").notNull().unique(),
  failedCount: integer("failed_count").default(0),
  blockedUntil: timestamp("blocked_until"),
  lastAttempt: timestamp("last_attempt").defaultNow(),
});

export const events = pgTable("events", {
  id: serial("id").primaryKey(),
  businessId: integer("business_id").notNull().references(() => businesses.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description"),
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date"),
  location: text("location"),
  isVirtual: boolean("is_virtual").default(false),
  virtualLink: text("virtual_link"),
  imageUrl: text("image_url"),
  maxAttendees: integer("max_attendees"),
  registrationUrl: text("registration_url"),
  isPublished: boolean("is_published").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const businessAnalytics = pgTable("business_analytics", {
  id: serial("id").primaryKey(),
  businessId: integer("business_id").notNull().references(() => businesses.id, { onDelete: "cascade" }),
  date: timestamp("date").notNull(),
  views: integer("views").default(0),
  profileViews: integer("profile_views").default(0),
  websiteClicks: integer("website_clicks").default(0),
  phoneClicks: integer("phone_clicks").default(0),
  emailClicks: integer("email_clicks").default(0),
  adClicks: integer("ad_clicks").default(0),
  uniqueVisitors: integer("unique_visitors").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const auditLogs = pgTable("audit_logs", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id),
  action: text("action").notNull(),
  entityType: text("entity_type").notNull(),
  entityId: integer("entity_id"),
  message: text("message"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const emailDnsSettings = pgTable("email_dns_settings", {
  id: serial("id").primaryKey(),
  forwardingFrom: text("forwarding_from"),
  forwardingTo: text("forwarding_to"),
  hostingerMx: text("hostinger_mx").array(),
  hostingerSpf: text("hostinger_spf"),
  hostingerDkim: text("hostinger_dkim"),
  hostingerDmarc: text("hostinger_dmarc"),
  workspaceMx: text("workspace_mx").array(),
  workspaceRouting: text("workspace_routing"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  token: text("token").notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  used: boolean("used").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

// === RELATIONS ===
export const usersRelations = relations(users, ({ many }) => ({
  businesses: many(businesses),
  reviews: many(reviews),
  comments: many(comments),
  notifications: many(notifications),
  loginHistory: many(loginHistory),
  apiKeys: many(apiKeys),
  passwordResetTokens: many(passwordResetTokens),
}));

export const passwordResetTokensRelations = relations(passwordResetTokens, ({ one }) => ({
  user: one(users, { fields: [passwordResetTokens.userId], references: [users.id] }),
}));

export const categoriesRelations = relations(categories, ({ many }) => ({
  businesses: many(businesses),
}));

export const businessesRelations = relations(businesses, ({ one, many }) => ({
  category: one(categories, { fields: [businesses.categoryId], references: [categories.id] }),
  owner: one(users, { fields: [businesses.ownerId], references: [users.id] }),
  reviews: many(reviews),
  comments: many(comments),
  events: many(events),
  analytics: many(businessAnalytics),
}));

export const reviewsRelations = relations(reviews, ({ one }) => ({
  business: one(businesses, { fields: [reviews.businessId], references: [businesses.id] }),
  user: one(users, { fields: [reviews.userId], references: [users.id] }),
}));

export const commentsRelations = relations(comments, ({ one }) => ({
  business: one(businesses, { fields: [comments.businessId], references: [businesses.id] }),
  user: one(users, { fields: [comments.userId], references: [users.id] }),
}));

export const eventsRelations = relations(events, ({ one }) => ({
  business: one(businesses, { fields: [events.businessId], references: [businesses.id] }),
}));

export const businessAnalyticsRelations = relations(businessAnalytics, ({ one }) => ({
  business: one(businesses, { fields: [businessAnalytics.businessId], references: [businesses.id] }),
}));

// === INSERT SCHEMAS ===
export const insertUserSchema = createInsertSchema(users).omit({ id: true, createdAt: true });
export const insertCategorySchema = createInsertSchema(categories).omit({ id: true });
export const insertBusinessSchema = createInsertSchema(businesses).omit({ id: true, createdAt: true, updatedAt: true, avgRating: true, reviewCount: true, slug: true });
export const insertReviewSchema = createInsertSchema(reviews).omit({ id: true, createdAt: true });
export const insertCommentSchema = createInsertSchema(comments).omit({ id: true, createdAt: true });
export const insertAdSchema = createInsertSchema(ads).omit({ id: true, createdAt: true });
export const insertEventSchema = createInsertSchema(events).omit({ id: true, createdAt: true, updatedAt: true });
export const insertEmailDnsSettingsSchema = createInsertSchema(emailDnsSettings).omit({ id: true, updatedAt: true });
export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens).omit({ id: true, createdAt: true });

// ─── Input Sanitization Helpers ──────────────────────────────────────────────

// Reject strings containing potential HTML/script injection
const noHtmlPattern = /<[^>]*>/;
const noScriptPattern = /javascript:/i;
const noDataUriPattern = /data:/i;

function createSafeTextSchema(minLen: number, maxLen: number, fieldName: string) {
  return z.string()
    .min(minLen, `${fieldName} must be at least ${minLen} characters.`)
    .max(maxLen, `${fieldName} must be ${maxLen} characters or fewer.`)
    .refine((val) => !noHtmlPattern.test(val), `${fieldName} cannot contain HTML tags.`)
    .refine((val) => !noScriptPattern.test(val), `${fieldName} cannot contain script references.`)
    .refine((val) => !noDataUriPattern.test(val), `${fieldName} cannot contain data URIs.`);
}

// Password strength validation helper
const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters.")
  .max(128, "Password must be 128 characters or fewer.")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter.")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter.")
  .regex(/[0-9]/, "Password must contain at least one number.");

// Phone number validation (E.164 format or common formats)
const phoneSchema = z.string()
  .max(30, "Phone number must be 30 characters or fewer.")
  .regex(/^\+?[\d\s\-()]+$/, "Phone number can only contain digits, spaces, +, -, and parentheses.")
  .optional()
  .nullable();

// URL validation - reject data URIs and javascript URLs
const safeUrlSchema = z.string()
  .url("Please enter a valid URL.")
  .max(500, "URL must be 500 characters or fewer.")
  .refine((val) => !noDataUriPattern.test(val), "URL cannot be a data URI.")
  .refine((val) => !noScriptPattern.test(val), "URL cannot contain script references.");

// ─── AUTH SCHEMAS — with clear human-readable error messages ─────────────────
export const registerSchema = z.object({
  username: z.string()
    .min(3, "Username must be at least 3 characters.")
    .max(30, "Username must be 30 characters or fewer.")
    .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores.")
    .refine((val) => !noHtmlPattern.test(val), "Username cannot contain HTML tags."),
  email: z.string().email("Please enter a valid email address.").max(254),
  password: passwordSchema,
  recaptchaToken: z.string().min(1, "Captcha verification is required.").max(2048),
});

export const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  password: z.string().min(1, "Password is required.").max(128),
  recaptchaToken: z.string().min(1, "Captcha verification is required.").max(2048),
});

export const updatePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required.").max(128),
  newPassword: passwordSchema,
});

// === REQUEST / RESPONSE TYPES ===
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type RegisterRequest = z.infer<typeof registerSchema>;
export type LoginRequest = z.infer<typeof loginSchema>;

export type Category = typeof categories.$inferSelect;
export type InsertCategory = z.infer<typeof insertCategorySchema>;

export type Business = typeof businesses.$inferSelect;
export type InsertBusiness = z.infer<typeof insertBusinessSchema>;
export type UpdateBusinessRequest = Partial<InsertBusiness>;

export type Review = typeof reviews.$inferSelect;
export type InsertReview = z.infer<typeof insertReviewSchema>;

export type Comment = typeof comments.$inferSelect;
export type InsertComment = z.infer<typeof insertCommentSchema>;

export type Ad = typeof ads.$inferSelect;
export type InsertAd = z.infer<typeof insertAdSchema>;

export type Notification = typeof notifications.$inferSelect;

export type PushSubscription = typeof pushSubscriptions.$inferSelect;
export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({ id: true, createdAt: true });
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;

export type LoginHistory = typeof loginHistory.$inferSelect;
export type ApiKey = typeof apiKeys.$inferSelect;
export type LoginBlock = typeof loginBlocks.$inferSelect;

export type Event = typeof events.$inferSelect;
export type InsertEvent = z.infer<typeof insertEventSchema>;

export type BusinessAnalytics = typeof businessAnalytics.$inferSelect;

export type EmailDnsSettings = typeof emailDnsSettings.$inferSelect;
export type InsertEmailDnsSettings = z.infer<typeof insertEmailDnsSettingsSchema>;
export type AuditLog = typeof auditLogs.$inferSelect;
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;

// === EXTENDED RESPONSE TYPES (with joins) ===
export type BusinessWithDetails = Business & {
  category?: Category | null;
  owner?: Pick<User, "id" | "username" | "avatarUrl"> | null;
};

export type ReviewWithUser = Review & {
  user?: Pick<User, "id" | "username" | "avatarUrl">;
};

export type CommentWithUser = Comment & {
  user?: Pick<User, "id" | "username" | "avatarUrl">;
};

// === QUERY PARAMS ===
export interface BusinessQueryParams {
  search?: string;
  categoryId?: number;
  isOnline?: boolean;
  isInPerson?: boolean;
  city?: string;
  lat?: number;
  lng?: number;
  radiusKm?: number;
  isFeatured?: boolean;
  status?: "pending" | "approved" | "rejected" | "all" | "archived";
  includeArchived?: boolean;
  archivedOnly?: boolean;
  page?: number;
  limit?: number;
}

// === PAGINATED RESPONSE ===
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// === ADMIN STATS ===
export interface AdminStats {
  totalBusinesses: number;
  pendingSubmissions: number;
  totalUsers: number;
  totalReviews: number;
  totalComments: number;
  totalAds: number;
  approvedToday: number;
  featuredListings: number;
}

// === CURRENT USER RESPONSE ===
export type CurrentUserResponse = Pick<User, "id" | "username" | "email" | "role" | "avatarUrl"> | null;

// === DURHAM REGION MUNICIPALITIES ===
export const DURHAM_MUNICIPALITIES = [
  "Oshawa",
  "Pickering",
  "Ajax",
  "Whitby",
  "Clarington",
  "Uxbridge",
  "Scugog",
  "Brock",
] as const;

export type DurhamMunicipality = typeof DURHAM_MUNICIPALITIES[number];
